// src/data/coverage/instrumentFragments.ts
// ============================================================
// THE SHAPE OF EACH INSTRUMENT, TAKEN FROM THE COMPLETED ACME WORKBOOK
//
// The Document Upload page shows a client what a template asks for before asking them to
// download it: column names for free, then a rendered slice of the real completed example,
// then the download. This file is that slice.
//
// ─────────────────────────────────────────────────────────────────────────────
// EVERY CELL BELOW IS COPIED FROM public/templates, AND A TEST PROVES IT.
//
// The fragments are baked rather than fetched, because a page that reads three xlsx files
// over the network to render its first paint has three new ways to fail in front of a
// room. The cost of baking is drift, so instrumentFragments.test.ts reads the real
// workbooks and asserts every column name, every cell, the row counts and the derived
// numbers. Change a workbook and the test fails until this file is updated with it.
//
// It replaces a set of composed placeholders: the design was written without the repo
// reachable, so it showed Epic Clarity, Snowflake and Imaging PACS at 11 and 14 years for
// D3, and ED boarding and nursing turnover for D2. None of that is in the real files.
// ─────────────────────────────────────────────────────────────────────────────
//
// MEASURE NAMES ARE NOT HERE. The page renders them through displayName(id) off the
// instrument's own measureIds, so a measure cannot be named twice in two places.
// ============================================================

import type { InstrumentId } from './gapInstruments';

export interface FragmentColumn {
  /** The column header, verbatim from the workbook. */
  name: string;
  /** Annotation pin, when this column carries a note. */
  pin?: string;
}

export interface FragmentNote {
  n: string;
  /** What the pinned column does to scoring. Must describe the real parser. */
  t: string;
}

export interface InstrumentFragment {
  /** The workbook this slice came from, under public/templates. */
  sourceFile: string;
  /** Sheet name, so the test can assert it parsed as the right template. */
  sheetName: string;
  columns: FragmentColumn[];
  /** First three data rows, cells aligned to `columns`. */
  rows: string[][];
  /** Total data rows in the workbook. `rows.length` is what is shown. */
  totalRows: number;
  notes: FragmentNote[];
  /**
   * The "what these rows produced" line, split around the measure count so the count can
   * render from the binding array rather than being typed into the sentence.
   */
  derivedPre: string;
  derivedPost: string;
}

export const INSTRUMENT_FRAGMENTS: Readonly<
  Record<Exclude<InstrumentId, 'pathA'>, InstrumentFragment>
> = {
  // ---- D2, the lightest ask ------------------------------------------------
  D2: {
    sourceFile: 'D2_Board_Priorities_Extract_ACME_EXAMPLE.xlsx',
    sheetName: 'Board_Priorities',
    columns: [
      { name: 'Priority' },
      { name: 'Primary theme', pin: '1' },
      { name: 'Owner' },
      { name: 'Target metric' },
      { name: 'Horizon' },
      { name: 'Funded this FY', pin: '2' },
    ],
    rows: [
      [
        'Reduce avoidable readmissions',
        'Value-Based Care',
        'Chief Medical Officer',
        '30-day all-cause readmission rate',
        'FY26',
        'Yes',
      ],
      [
        'Improve patient throughput',
        'Operational Efficiency',
        'Chief Operating Officer',
        'Median admission decision to bed placement',
        'FY26',
        'Yes',
      ],
      [
        'Grow ambulatory access',
        'Growth and Viability',
        'Chief Strategy Officer',
        'New patient third next available appointment',
        'FY26 to FY27',
        'Yes',
      ],
    ],
    totalRows: 7,
    notes: [
      {
        n: '1',
        t: 'The theme is what binds a priority to a measure. Leave it blank and the wording is read instead, and the result says that it was inferred.',
      },
      {
        n: '2',
        t: 'Yes, partially, or no reads straight into the score. Leave it honest.',
      },
    ],
    derivedPre: 'Read 7 priorities, 4 of them funded. ',
    derivedPost:
      ' now score from your document, including the priorities you have not set, which is often the more useful half.',
  },

  // ---- D1 ------------------------------------------------------------------
  D1: {
    sourceFile: 'D1_Staffing_and_Capacity_Inventory_ACME_EXAMPLE.xlsx',
    sheetName: 'Staffing_Capacity',
    columns: [
      { name: 'Team or role group' },
      { name: 'Total FTE' },
      { name: 'Percent allocated to active projects', pin: '1' },
      { name: 'Unallocated capacity (FTE)', pin: '2' },
      { name: 'Key skill areas' },
    ],
    rows: [
      [
        'Enterprise Data Engineering',
        '5',
        '88%',
        '0.6',
        'Data engineering, pipeline development, warehouse administration, integration and interfaces',
      ],
      [
        'Analytics and Reporting',
        '6',
        '90%',
        '0.6',
        'Reporting, self-service enablement, quality and regulatory measures, ad hoc analysis',
      ],
      [
        'Clinical Informatics',
        '3',
        '70%',
        '0.9',
        'Clinical workflow design, order set and documentation build, clinician liaison',
      ],
    ],
    totalRows: 3,
    notes: [
      {
        n: '1',
        t: 'What is left matters more than what you have. 70 percent committed across three people reads differently from 70 percent across nine.',
      },
      {
        n: '2',
        t: 'This column is what resource availability scores from. 2.1 free FTE against 14 total is a very different answer from 2.1 against 4.',
      },
    ],
    derivedPre: 'Read 3 team rows, 2.1 FTE unallocated against 14 total. ',
    derivedPost:
      ' now compare what an opportunity requires against capacity you actually have free, rather than an industry assumption.',
  },

  // ---- D3, the heaviest ask and the biggest mover ---------------------------
  D3: {
    sourceFile: 'D3_Data_Asset_and_Platform_Inventory_ACME_EXAMPLE.xlsx',
    sheetName: 'Data_Asset_Inventory',
    columns: [
      { name: 'Platform or data domain' },
      { name: 'Vendor or product' },
      { name: 'Historical depth (years)' },
      { name: 'Refresh cadence', pin: '1' },
      { name: 'Standards support' },
      { name: 'Self-rated quality (1 to 5)', pin: '2' },
    ],
    rows: [
      ['Core EHR', 'Epic', '9', 'Daily batch', 'HL7 v2 and FHIR', '4'],
      ['Enterprise data warehouse', 'Epic Caboodle', '9', 'Daily batch', 'Internal model, partial FHIR', '4'],
      ['Revenue cycle', 'Epic Resolute', '9', 'Daily batch', 'X12 837 and 835', '3'],
    ],
    totalRows: 9,
    notes: [
      {
        n: '1',
        t: 'Real time or near real time in this column is the only thing that scores real-time capability. It is never inferred from anywhere else.',
      },
      {
        n: '2',
        t: 'Quality is your own rating on purpose. What gets read is the gap between it and the cadence and standards you list beside it.',
      },
    ],
    derivedPre: 'Read 9 platform rows. 2 of the 9 refresh in real time or near real time. ',
    derivedPost:
      ' rescored, with real-time capability set from your cells rather than assumed.',
  },

  // ---- D4, the largest remaining gain --------------------------------------
  D4: {
    sourceFile: 'D4_Governance_and_Compliance_Charter_ACME_EXAMPLE.xlsx',
    sheetName: 'Governance_Charter',
    columns: [
      { name: 'Domain or control area', pin: '1' },
      { name: 'Policy or framework in place' },
      { name: 'Owner' },
      { name: 'Maturity (1 to 5)', pin: '2' },
      { name: 'Last reviewed' },
      { name: 'Data domains it applies to' },
    ],
    rows: [
      [
        'Security and privacy',
        'HIPAA security rule program, annual third party risk assessment',
        'Chief Information Security Officer',
        '4',
        '2026-03',
        'All clinical and financial domains',
      ],
      [
        'De-identification',
        'Documented Safe Harbor method, expert determination for research releases',
        'Privacy Officer',
        '3',
        '2025-11',
        'Research and analytics extracts',
      ],
      [
        'Regulatory framework alignment',
        'HIPAA, 42 CFR Part 2, state privacy statute mapping',
        'Compliance Officer',
        '4',
        '2026-01',
        'All domains',
      ],
    ],
    totalRows: 9,
    notes: [
      {
        n: '1',
        t: 'The control area is what binds a row to a measure, matched on the words you use rather than on the order of the rows. Reorder them, or leave one out, and the rest still land correctly.',
      },
      {
        n: '2',
        t: 'Your rating is the score. Nothing rescales it, and a 2 tells us more than a generous 4 does.',
      },
    ],
    derivedPre: 'Read 9 control areas, average maturity 2.8. ',
    derivedPost:
      ' now score from your charter, including three that nothing else we ask for can reach.',
  },
};

/** How many rows the workbook holds beyond the three shown. Zero renders no line. */
export function extraRowCount(fragment: InstrumentFragment): number {
  return Math.max(0, fragment.totalRows - fragment.rows.length);
}
